import React from 'react';
import {
  CircularProgress,
  useTheme,
  Backdrop,
  Typography,
  Divider,
  Card,
  Box,
} from '@mui/material';
import EtPayPayment from './Etpay/EtPayPayment';
import FintocPayment from './Fintoc/FintocPayment';
import analytics from '../../utils/analytics';
import {
  postExternalSubscriptionIntent,
  getBankAccount,
  getUserSubscriptions,
  deleteExternalSubscription,
} from '../../queries/account';
import { GetnetMetadataDetails, FintocMetadataDetails } from '../../types/authorization';
import fintocLogo from '../../assets/fintoc.svg';
import getnetLogo from '../../assets/getnet_logo.webp';
import BancameAlert from '../Alert';
import useStyles from './styles';
import BankAccount from '../BankAccountCard';
import {
  getBankAccountResponse,
  ExternalSubscriptionItem,
} from '../../types/account';
import AddBankAccountDialog from '../AddBankAccountDialog';
import PayYourLoan from '../../feature/payYourLoan';
import PatCardDetails from '../PatCardDetails';
import {
  PreviousSubscriptionRow,
  LastChangeInfo,
  SubscriptionActions,
} from './components';

interface PaymentWidgetProps {
  token: string;
  idNumber: string;
  paymentMethodProvider: 'fintoc' | 'etpay' | 'getnet';
}

const subscribedStates = ['PENDING', 'ACTIVE'];
const pacLogo = 'https://assets.banca.me/icons/4.png';
const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

const PAYMENT_PROVIDERS = {
  fintoc: FintocPayment,
  etpay: EtPayPayment,
};

export default function PaymentWidget({
  token,
  idNumber,
  paymentMethodProvider,
}: PaymentWidgetProps) {
  const classes = useStyles();
  const theme = useTheme();
  const [bankAccount, setBankAccount] = React.useState<getBankAccountResponse['data']['bank']>(null);
  const [loading, setLoading] = React.useState(false);
  const [widgetToken, setWidgetToken] = React.useState<string | undefined>(undefined);
  const [widgetToken2, setWidgetToken2] = React.useState<string | undefined>(undefined);
  const [openAlert, setOpenAlert] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('¡Pago (PAC) configurado correctamente!');
  const [errorMessage, setErrorMessage] = React.useState('Ha ocurrido un error, por favor contáctanos');
  const [enableChangePaymentMethod, setEnableChangePaymentMethod] = React.useState(false);
  const [subscriptions, setSubscriptions] = React.useState<ExternalSubscriptionItem[]>([]);

  const paymentMethod2 = paymentMethodProvider === 'etpay' ? 'fintoc' : 'etpay';

  const activeSubscription = subscriptions.find(
    (s) => s.isPrimary && subscribedStates.includes(s.subscriptionStatus || ''),
  );
  const isAlreadySubscribed = !!activeSubscription;
  const latestPreviousSub = [...subscriptions]
    .filter((s) => !s.isPrimary && subscribedStates.includes(s.subscriptionStatus || ''))
    .sort((a, b) => new Date(b.lastChangedAt || b.createdAt).getTime()
      - new Date(a.lastChangedAt || a.createdAt).getTime())[0];

  const showAlert = (success: boolean, message: string) => {
    setError(!success);
    if (success) setSuccessMessage(message); else setErrorMessage(message);
    setOpenAlert(false);
    setOpenAlert(true);
  };

  const loadUserSubscriptions = React.useCallback(async () => {
    try {
      const res = await getUserSubscriptions(token);
      setSubscriptions(res.data || []);
    } catch { /* silently fail */ }
  }, [token]);

  const suscriptionIntent = async (otherPaymentMethod = false) => {
    setOpenAlert(false);
    setLoading(true);
    setError(false);
    try {
      const provider = otherPaymentMethod ? paymentMethod2 : paymentMethodProvider;
      if (!(provider in PAYMENT_PROVIDERS)) {
        setLoading(false);
        return false;
      }
      const res = await postExternalSubscriptionIntent(token, provider as 'fintoc' | 'etpay');
      setLoading(false);
      if (subscribedStates.includes(res.subscriptionStatus)) {
        analytics.page('PU - PAGOS', 'PAC YA CONFIGURADO');
        await loadUserSubscriptions();
        return true;
      }
      analytics.page('PU - PAGOS', 'CONFIGURACION PAC');
      if (otherPaymentMethod) setWidgetToken2(res.widgetToken);
      else setWidgetToken(res.widgetToken);
      return true;
    } catch {
      showAlert(false, 'Ha ocurrido un error, por favor contáctanos');
      setLoading(false);
      return false;
    }
  };

  const initSubscriptionState = async () => {
    setLoading(true);
    try {
      const res = await getUserSubscriptions(token);
      const subs = res.data || [];
      setSubscriptions(subs);
      const primary = subs.find((s) => s.isPrimary && subscribedStates.includes(s.subscriptionStatus || ''));
      if (primary) {
        analytics.page('PU - PAGOS', `${primary.paymentMethod.toUpperCase()} YA CONFIGURADO`);
        setLoading(false);
      } else {
        await suscriptionIntent();
      }
    } catch {
      await suscriptionIntent();
    }
  };

  const pollSubscriptions = async (
    condition: (subs: ExternalSubscriptionItem[]) => ExternalSubscriptionItem | undefined,
    onFound: (subs: ExternalSubscriptionItem[], match: ExternalSubscriptionItem) => void,
    onTimeout: () => void,
  ) => {
    let retriesCount = 0;
    const MAX_RETRIES = 15;

    const poll = async (): Promise<void> => {
      const res = await getUserSubscriptions(token);
      const subs = res.data || [];
      const match = condition(subs);
      if (match) {
        setSubscriptions(subs);
        onFound(subs, match);
        return;
      }
      retriesCount += 1;
      if (retriesCount < MAX_RETRIES) {
        await delay(2000);
        await poll();
      } else {
        throw new Error('Timeout');
      }
    };

    try {
      await poll();
    } catch {
      onTimeout();
    }
  };

  const checkSubscriptionStatus = async () => {
    setError(false);
    setLoading(true);
    setOpenAlert(false);
    let success = false;
    await pollSubscriptions(
      (subs) => subs.find((s) => s.isPrimary && subscribedStates.includes(s.subscriptionStatus || '')),
      (_, primary) => {
        setLoading(false);
        showAlert(true, '¡Pago (PAC) configurado correctamente!');
        analytics.page('PU - PAGOS', `${primary.paymentMethod.toUpperCase()} YA CONFIGURADO`);
        success = true;
      },
      () => {
        setLoading(false);
        showAlert(false, 'Ha ocurrido un error, por favor contáctanos');
      },
    );
    return success;
  };

  const checkNewSubscriptionStatus = async (subscriptionId?: string) => {
    if (!subscriptionId) return;
    setLoading(true);
    await pollSubscriptions(
      (subs) => subs.find((s) => s.id === subscriptionId && subscribedStates.includes(s.subscriptionStatus || '')),
      () => {
        setLoading(false);
        showAlert(true, '¡Nueva cuenta PAC configurada correctamente!');
      },
      () => {
        setLoading(false);
        showAlert(false, 'Ha ocurrido un error verificando la nueva suscripción');
      },
    );
  };

  const handleError = ({ message, resetWidgetToken }: {
    message: string;
    resetWidgetToken?: boolean;
  }) => {
    showAlert(false, message);
    setEnableChangePaymentMethod(false);
    if (resetWidgetToken) setTimeout(() => suscriptionIntent(), 1500);
    setTimeout(() => setErrorMessage('Ha ocurrido un error, por favor contáctanos'), 5000);
  };

  const handleSuccess = async () => {
    const response = await checkSubscriptionStatus();
    if (!response) {
      await suscriptionIntent();
      setEnableChangePaymentMethod(false);
    }
  };

  const handleDeleteSubscription = async (subscriptionId: string) => {
    try {
      await deleteExternalSubscription(token, subscriptionId);
      showAlert(true, '¡Cuenta PAC eliminada correctamente!');
      await loadUserSubscriptions();
    } catch {
      showAlert(false, 'Ha ocurrido un error al eliminar la cuenta');
    }
  };

  const onExit = () => {
    setSuccessMessage('Si no configuras PAC, no podremos cobrar automaticamente tu producto');
    setError(false);
    setOpenAlert(false);
    setOpenAlert(true);
  };

  const handleSwitchSuccess = async () => {
    await loadUserSubscriptions();
    showAlert(true, '¡Cuenta PAC cambiada correctamente!');
  };

  React.useEffect(() => {
    getBankAccount(token).then((res) => setBankAccount(res.data.bank)).catch(() => {});
    initSubscriptionState();
  }, [token]);

  const PaymentProvider = (paymentMethodProvider in PAYMENT_PROVIDERS)
    ? PAYMENT_PROVIDERS[paymentMethodProvider as 'fintoc' | 'etpay']
    : PAYMENT_PROVIDERS.fintoc;

  const PaymentProvider2 = PAYMENT_PROVIDERS[paymentMethod2];

  let body: React.ReactNode;
  let logo: React.ReactNode;

  if (isAlreadySubscribed && activeSubscription.paymentMethod === 'pac') {
    const metadata = activeSubscription.metadataDetails as FintocMetadataDetails;
    const detailRows = [
      { label: 'RUT', value: metadata?.holderRut },
      { label: 'Nombre', value: metadata?.holderName || metadata?.accountName },
      { label: 'Banco', value: metadata?.institutionName || metadata?.bank },
      { label: 'N° de cuenta', value: metadata?.accountNumber },
      { label: 'Tipo de cuenta', value: metadata?.accountType || metadata?.acountOfficialName },
    ].filter((row) => row.value);

    logo = <object className={classes.fintocLogo} data={fintocLogo} aria-labelledby="fintoc logo" />;
    body = (
      <>
        <Box sx={{ mx: 2.5, mt: 2.5, mb: 2 }}>
          <Typography variant="body1" align="center" gutterBottom>
            <Typography variant="inherit" color="secondary" fontWeight="bold" component="span">
              Tu pago automático (PAC) ya está configurado
            </Typography>
            {' '}
            con la siguiente cuenta bancaria asociada:
          </Typography>
        </Box>

        {detailRows.length > 0 && (
          <Box sx={{ width: '100%', px: 3 }}>
            {detailRows.map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                <Typography variant="body2" fontWeight="medium">{row.value}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {latestPreviousSub && <PreviousSubscriptionRow sub={latestPreviousSub} label="Cuenta anterior" />}

        <SubscriptionActions
          subscriptions={subscriptions}
          activeSubscription={activeSubscription}
          token={token}
          idNumber={idNumber}
          paymentMethodProvider={paymentMethodProvider}
          changeButtonLabel="Cambiar cuenta PAC"
          onSwitchSuccess={handleSwitchSuccess}
          onNewSubscriptionSuccess={checkNewSubscriptionStatus}
          onDeleteSubscription={handleDeleteSubscription}
        />

        <LastChangeInfo subscription={activeSubscription} />
      </>
    );
  } else if (isAlreadySubscribed && activeSubscription.paymentMethod === 'pat') {
    logo = <object className={classes.getnetLogo} data={getnetLogo} aria-labelledby="getnet logo" />;
    body = (
      <>
        <Box style={{ margin: 20 }}>
          <Typography variant="body1" align="center" gutterBottom>
            <Typography variant="inherit" color="primary" fontWeight="bold" component="span">
              Tu método de pago automático (
              {activeSubscription.paymentMethod.toUpperCase()}
              ) ya está configurado,
              <br />
              con la siguiente tarjeta asociada:
            </Typography>
          </Typography>
        </Box>

        <Box width="100%" sx={{ paddingInline: 2.5, marginBottom: 2.5 }}>
          {activeSubscription.metadataDetails && (
            <PatCardDetails
              metadata={activeSubscription.metadataDetails as GetnetMetadataDetails}
              title="Detalles de la tarjeta"
            />
          )}
        </Box>

        {latestPreviousSub && <PreviousSubscriptionRow sub={latestPreviousSub} label="Tarjeta anterior" />}

        <SubscriptionActions
          subscriptions={subscriptions}
          activeSubscription={activeSubscription}
          token={token}
          idNumber={idNumber}
          paymentMethodProvider={paymentMethodProvider}
          changeButtonLabel="Cambiar tarjeta PAT"
          onSwitchSuccess={handleSwitchSuccess}
          onNewSubscriptionSuccess={checkNewSubscriptionStatus}
          onDeleteSubscription={handleDeleteSubscription}
        />

        <LastChangeInfo subscription={activeSubscription} />
      </>
    );
  } else if (isAlreadySubscribed) {
    logo = <img className={classes.pacLogo} src={pacLogo} aria-labelledby="pac logo" />;
    body = (
      <Box style={{ margin: 20 }}>
        <Typography variant="body1" align="center" gutterBottom>
          <Typography variant="inherit" color="secondary" fontWeight="bold" component="span">
            Tu método de pago automático (
            {activeSubscription.paymentMethod.toUpperCase()}
            ) ya está configurado.
          </Typography>
        </Typography>
      </Box>
    );
  } else {
    logo = <img className={classes.pacLogo} src={pacLogo} aria-labelledby="pac logo" />;
    body = (
      <Box style={{ margin: 20 }}>
        <Typography variant="body2" align="center" gutterBottom>
          Estimado usuario, para acceder a nuestros productos de
          financiamiento es necesario
          {' '}
          <b style={{ color: theme.palette.secondary.main }}>
            suscribir un PAC (Pago Automático de Cuentas)
          </b>
          {' '}
          para recibir tu crédito.
        </Typography>
        <Typography variant="body2" align="center">
          Debes tener a mano tus credenciales bancarias (e.g. tarjeta de
          coordenadas o digipass). Haz click en
          {' '}
          <b style={{ color: theme.palette.secondary.main }}>Asociar PAC</b>
          {' '}
          para comenzar el proceso.
        </Typography>
        <div style={{ justifyContent: 'center', display: 'flex' }}>
          <PaymentProvider
            widgetToken={widgetToken}
            idNumber={idNumber}
            onError={handleError}
            onSuccess={handleSuccess}
            onExit={onExit}
          />
        </div>
        {enableChangePaymentMethod && widgetToken2 && (
          <div style={{ justifyContent: 'center', display: 'flex', paddingBlock: 10 }}>
            <PaymentProvider2
              widgetToken={widgetToken2}
              idNumber={idNumber}
              onError={handleError}
              onSuccess={handleSuccess}
              onExit={onExit}
              label="Cambiar proveedor de pago"
            />
          </div>
        )}
      </Box>
    );
  }

  const showBankAccountFooter = !isAlreadySubscribed || activeSubscription?.paymentMethod === 'pat';
  const showPayYourLoanFooter = !loading && (!isAlreadySubscribed
    || (activeSubscription?.paymentMethod !== 'pac' && activeSubscription?.paymentMethod !== 'pat'));

  let footer: React.ReactNode = null;
  if (showBankAccountFooter) {
    footer = !bankAccount ? (
      <div style={{ textAlign: 'center', width: '100%' }}>
        <AddBankAccountDialog token={token} />
      </div>
    ) : (
      <Card
        className={classes.card}
        sx={{ borderRadius: '15px', width: { xs: '100%', md: '80%' } }}
      >
        <BankAccount title="Cuenta bancaria asociada" bankAccount={bankAccount} />
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Card sx={{ borderRadius: '20px', padding: 2.5, width: { xs: '100%', md: '80%' } }}>
        <div style={{
          marginBottom: paymentMethodProvider === 'fintoc' ? 10 : 0,
          textAlign: 'center',
          marginTop: paymentMethodProvider === 'fintoc' ? 20 : 15,
        }}
        >
          {logo}
        </div>
        <Divider />
        <div className={classes.content}>
          {body}
          <BancameAlert
            successMessage={successMessage}
            errorMessage={errorMessage}
            openAlert={openAlert}
            error={error}
          />
          <Backdrop className={classes.backdrop} open={loading}>
            <div>
              <CircularProgress color="inherit" />
            </div>
            <b style={{ marginLeft: 20 }}>Cargando, espera un momento...</b>
          </Backdrop>
        </div>
      </Card>
      <br />
      {footer}
      <br />
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
        {showPayYourLoanFooter && (
          <>
            <PayYourLoan token={token} pageCategory="PU - PAGOS" />
            <br />
          </>
        )}
      </Box>
    </Box>
  );
}
